import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { cuentaRegresiva, fechaCompleta, fechaLarga, plural } from '../../../lib/format'
import { BOTON_SOBRE_OSCURO, cx, tieneTexto, urlSegura } from './comunes'

/* ===========================================================================
   Portada — la pantalla completa que abre la invitación.

   Fondo tinta (el mismo del riel del cordón, así se funden en el borde), los
   nombres enormes en font-display y el segundo desplazado a la derecha: la
   asimetría es deliberada, es lo que separa esta página de la plantilla.
   =========================================================================== */

export interface PortadaProps {
  partner1Name: string
  partner2Name: string
  weddingDate: string | null
  tagline: string
  /** Lo que se lee en lugar de la fecha mientras no hay fecha. */
  dateNote: string
  imageUrl: string
  /** Video corto de fondo. Si está vacío, manda la foto. */
  videoUrl: string
  /** Texto libre o fecha ISO: la pareja escribe lo que quiere. */
  fechaLimite: string
  mostrarCuenta: boolean
  /** Después de la boda ya no se pide confirmación: se mira la galería. */
  pasada: boolean
  urlConfirmar: string
  /**
   * El día de la boda la portada calla el RSVP. El botón flotante ya invita a
   * compartir fotos ese día; dejar aquí «Confirmar asistencia» hacía que la
   * misma pantalla pidiera dos cosas distintas a la vez.
   */
  esHoy: boolean
}

/** Quien pide menos movimiento no recibe un video reproduciéndose solo. */
function prefiereMenosMovimiento(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function Portada({
  partner1Name,
  partner2Name,
  weddingDate,
  tagline,
  dateNote,
  imageUrl,
  videoUrl,
  fechaLimite,
  mostrarCuenta,
  pasada,
  urlConfirmar,
  esHoy,
}: PortadaProps) {
  const [fondoRoto, setFondoRoto] = useState(false)
  const [videoRoto, setVideoRoto] = useState(false)
  const [quieto] = useState(prefiereMenosMovimiento)
  const fondo = urlSegura(imageUrl)
  const video = urlSegura(videoUrl)
  const fecha = fechaCompleta(weddingDate)
  const limite = fechaLarga(fechaLimite) || fechaLimite.trim()

  // El video manda si la pareja lo puso, el navegador lo reproduce y nadie pidió
  // quietud. En cualquier otro caso queda la foto, que es lo que había siempre.
  const videoDeFondo = videoRoto || quieto ? null : video
  const fotoDeFondo = videoDeFondo !== null || fondoRoto ? null : fondo

  return (
    <header
      id="portada"
      data-nudo=""
      className="relative isolate flex min-h-dvh flex-col justify-end bg-tinta px-5 pb-12 pt-16 sm:px-10 sm:pb-16 lg:px-16"
    >
      {videoDeFondo !== null && (
        <video
          src={videoDeFondo}
          poster={fondo ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          onError={() => setVideoRoto(true)}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
      )}

      {fotoDeFondo !== null && (
        <img
          src={fotoDeFondo}
          alt=""
          aria-hidden="true"
          onError={() => setFondoRoto(true)}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
      )}

      {/* Velo plano, no degradado: el texto tiene que leerse al sol. */}
      {(videoDeFondo !== null || fotoDeFondo !== null) && (
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-tinta/72" />
      )}

      {/* Centrado como las secciones de abajo: comparten eje. La asimetría de
          los nombres (pl-[4%], pl-[10%]) es interna al bloque y se conserva. */}
      <div className="mx-auto w-full max-w-5xl">
        <p className="eyebrow text-laton-claro">{fecha === '' ? dateNote : fecha}</p>

        {/* Los nombres son entrada libre y el texto del sistema puede ir al 200%:
            el nombre se parte antes que perder letras contra el borde. */}
        <h1 className="mt-4 font-display text-nombre text-bruma [overflow-wrap:anywhere] hyphens-auto">
          <span className="block">{partner1Name}</span>
          {/* La conjunción acompaña a los nombres, no se esconde: latón, con
              cuerpo propio, aunque siempre por debajo de los dos nombres. */}
          <span className="my-3 block pl-[4%] font-sans text-lg uppercase tracking-[0.35em] text-laton-claro sm:my-4 sm:text-2xl">
            y
          </span>
          <span className="block pl-[10%]">{partner2Name}</span>
        </h1>

        {tagline.trim() !== '' && (
          <p className="mt-6 max-w-md text-lg leading-relaxed text-palo">{tagline}</p>
        )}

        {mostrarCuenta && weddingDate && <CuentaRegresiva fecha={weddingDate} />}

        {!pasada && !esHoy && (
          <div className="mt-8">
            <Link to={urlConfirmar} className={BOTON_SOBRE_OSCURO}>
              Confirmar asistencia
            </Link>
            {tieneTexto(limite) && (
              <p className="mt-4 text-sm text-palo">Confírmanos antes del {limite}.</p>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Cuenta regresiva
// ---------------------------------------------------------------------------

function CuentaRegresiva({ fecha }: { fecha: string }) {
  const [ahora, setAhora] = useState(() => Date.now())
  const restante = cuentaRegresiva(fecha, ahora)
  const pasada = restante?.pasada ?? true

  // Un intervalo que se apaga solo cuando ya no hay nada que contar.
  useEffect(() => {
    if (pasada) return
    const id = window.setInterval(() => setAhora(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [pasada])

  if (!restante) return null

  if (restante.pasada) {
    return (
      <p className="mt-10 max-w-md text-base leading-relaxed text-palo">
        Ya nos casamos. Gracias por acompañarnos: aquí abajo quedan las fotos y los mensajes de ese
        día.
      </p>
    )
  }

  const celdas = [
    { valor: restante.dias, etiqueta: 'días' },
    { valor: restante.horas, etiqueta: 'horas' },
    { valor: restante.minutos, etiqueta: 'minutos' },
    { valor: restante.segundos, etiqueta: 'segundos' },
  ]

  return (
    <div className="mt-8">
      {/* Los números cambian cada segundo: para el lector de pantalla basta el
          resumen en días, y los dígitos quedan ocultos. */}
      <p className="sr-only">Faltan {plural(restante.dias, 'día', 'días')} para la boda.</p>
      <div aria-hidden="true" className="flex flex-wrap gap-x-8 gap-y-4">
        {celdas.map((celda) => (
          <div key={celda.etiqueta}>
            <p className="tabular font-mono text-3xl text-bruma sm:text-4xl">
              {String(celda.valor).padStart(2, '0')}
            </p>
            <p className={cx('mt-1 text-[0.625rem] uppercase tracking-[0.22em] text-palo/80')}>
              {celda.etiqueta}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
